import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Check, Trash2, RefreshCw, Zap } from "lucide-react";

const KIND_LABEL = {
  trial_welcome: "Trial welcome", onboarding_reminder: "Onboarding reminder", plan_ready: "Plan ready",
  review_reminder: "Review reminder", trial_nudge: "Trial upgrade nudge", trial_followup: "Trial follow-up",
  coffee_reminder: "Coffee order reminder", birthday: "Birthday wish", notice: "Notice",
};

export const WhatsAppOutbox = ({ api }) => {
  const [data, setData] = useState(null);

  const load = () => api().get("/admin/whatsapp/outbox").then((r) => setData(r.data)).catch(() => toast.error("Could not load WhatsApp outbox"));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const markSent = async (id) => {
    try { await api().post(`/admin/whatsapp/outbox/${id}/mark-sent`); load(); } catch { toast.error("Failed"); }
  };
  const dismiss = async (id) => {
    try { await api().delete(`/admin/whatsapp/outbox/${id}`); load(); } catch { toast.error("Failed"); }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-[#89937C]" /></div>;

  return (
    <div data-testid="whatsapp-outbox">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-serif-display text-2xl tracking-tight">WhatsApp Outbox</div>
          <p className="text-sm text-[#5C665A]">
            {data.auto_send_enabled
              ? <span className="inline-flex items-center gap-1 text-[#34A853]"><Zap size={13} /> Auto-send is ON (Twilio connected)</span>
              : "Auto-send off — tap 'Send on WhatsApp' to deliver each message from your phone. Add Twilio credentials to automate."}
            {" · "}{data.optouts} opted out
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#5C665A] hover:text-[#2D372B]"><RefreshCw size={12} /> Refresh</button>
      </div>

      {data.pending.length === 0 ? (
        <p className="text-sm text-[#5C665A] bg-white border border-[#E5E2D9] rounded-2xl p-6 text-center" data-testid="whatsapp-outbox-empty">
          No pending WhatsApp messages — reminders will appear here automatically.
        </p>
      ) : (
        <div className="space-y-3">
          {data.pending.map((m) => (
            <div key={m.id} className="bg-white border border-[#E5E2D9] rounded-2xl p-4" data-testid="whatsapp-outbox-item">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-sm">
                  {m.name || "Client"} · +{m.phone_digits}
                  <span className="ml-2 rounded-full bg-[#89937C]/15 text-[#5C665A] text-[10px] font-semibold px-2 py-0.5">{KIND_LABEL[m.kind] || m.kind}</span>
                </div>
                <span className="text-[11px] text-[#9AA294]">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-[#5C665A] mt-2 whitespace-pre-wrap">{m.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={`https://wa.me/${m.phone_digits}?text=${encodeURIComponent(m.message)}`}
                  target="_blank" rel="noreferrer" data-testid="whatsapp-send-btn"
                  onClick={() => markSent(m.id)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium bg-[#25D366] text-white hover:opacity-90"
                >
                  <MessageCircle size={12} /> Send on WhatsApp
                </a>
                <button onClick={() => markSent(m.id)} data-testid="whatsapp-mark-sent-btn"
                  className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium border border-[#89937C]/50 text-[#5C665A] hover:bg-[#89937C]/10">
                  <Check size={12} /> Mark sent
                </button>
                <button onClick={() => dismiss(m.id)} data-testid="whatsapp-dismiss-btn"
                  className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium border border-[#A05252]/50 text-[#A05252] hover:bg-[#A05252]/10">
                  <Trash2 size={12} /> Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.recent.length > 0 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.15em] text-[#9AA294] mb-2">Recently handled</div>
          <div className="space-y-1.5">
            {data.recent.slice(0, 15).map((m) => (
              <div key={m.id} data-testid="whatsapp-recent-item" className="text-xs text-[#5C665A] bg-white border border-[#F0EEE6] rounded-xl px-3 py-2 flex justify-between gap-2">
                <span className="truncate">{m.name || "Client"} · +{m.phone_digits} · {KIND_LABEL[m.kind] || m.kind}</span>
                <span className="shrink-0 text-[#9AA294]">{m.status === "sent_auto" ? "auto-sent" : m.status === "sent_manual" ? "sent by you" : m.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
